import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Membership() {
  const { t } = useLanguage();
  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-3xl">
          <p className="overline">{t.membership.overline}</p>
          <h2 className="font-display uppercase mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            {t.membership.title}
          </h2>
          <p className="mt-5 text-base md:text-lg text-white/65 max-w-2xl">
            {t.membership.subtitle}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-0">
          {t.membership.tiers.map((tier, i) => {
            const highlighted = tier.highlight;
            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`membership-card-${i}`}
                className={`relative p-8 md:p-10 flex flex-col ${
                  highlighted
                    ? "border-2 border-redflag lg:-my-4 lg:z-10 bg-[#141414] glow-red"
                    : "border border-white/12 bg-[#0F0F0F]"
                } ${!highlighted && i === 0 ? "lg:border-r-0" : ""} ${!highlighted && i === 2 ? "lg:border-l-0" : ""}`}
              >
                {highlighted && tier.badge && (
                  <span className="absolute -top-3 left-8 bg-redflag text-white px-3 py-1 font-mono-plex text-[10px] tracking-widest uppercase">
                    {tier.badge}
                  </span>
                )}
                <div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight text-white">
                    {tier.name}
                  </h3>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed min-h-[3rem]">
                    {tier.desc}
                  </p>
                </div>
                <div className="mt-6 mb-8 flex items-baseline gap-2">
                  <div className="font-display text-4xl md:text-5xl tracking-tight text-white">
                    {tier.price}
                  </div>
                  {tier.period && (
                    <div className="text-white/45 font-mono-plex text-sm">
                      {tier.period}
                    </div>
                  )}
                </div>
                <ul className="space-y-3 flex-1">
                  {tier.features.map((f, fi) => (
                    <li
                      key={fi}
                      className="flex items-start gap-3 text-sm md:text-[15px] text-white/80"
                    >
                      <Check
                        size={18}
                        strokeWidth={2}
                        className={`mt-0.5 ${highlighted ? "text-redflag" : "text-white/80"}`}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  data-testid={`membership-cta-${i}`}
                  className={`mt-10 inline-flex items-center justify-center px-5 py-3.5 font-medium text-sm uppercase tracking-wide transition-colors ${
                    highlighted ? "btn-primary" : "btn-ghost"
                  }`}
                >
                  {tier.cta}
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
